/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { declare, level, NESTED_ACTION, permission, permissionSet } from '@gh/core-auth';

export enum PermissionActions {
  Create = 'CREATE',
  Edit = 'EDIT',
  CreateEdit = 'CREATE|EDIT',
  CreateEditView = 'CREATE|EDIT|VIEW',
  EditView = 'EDIT|VIEW',
  ProduceView = 'PRODUCE|VIEW',
  Delete = 'DELETE',
  Undelete = 'UNDELETE',
  View = 'VIEW',
  List = 'LIST',
  Any = 'ANY',
  Activate = 'ACTIVATE',
  Deactivate = 'DEACTIVATE',
  Execute = 'EXECUTE',
  Manage = 'MANAGE',
  Approve = 'APPROVE',
  Unapprove = 'UNAPPROVE',
  Reassign = 'REASSIGN',
  Complete = 'COMPLETE',
  Redo = 'REDO',
  UncompleteComplete = 'UNCOMPLETE|COMPLETE',
  Unfinalize = 'UNFINALIZE',
  Authorize = 'AUTHORIZE',
  Produce = 'PRODUCE',
  Initiate = 'INITIATE',
  Import = 'IMPORT',
}

export const BuilderPermission = declare({
  Dashboard: level('DASHBOARD', () => ({
    NESTED: permission(NESTED_ACTION),
    Franchise: level('FRANCHISE', () => ({
      VIEW: permission(PermissionActions.View),
      MANAGE: permission(PermissionActions.Manage),
    })),
    Sales: level('SALES', () => ({
      VIEW: permission(PermissionActions.View),
      MANAGE: permission(PermissionActions.Manage),
    })),
  })),
  Contacts: level('CONTACTS', () => ({
    NESTED: permission(NESTED_ACTION),
    CityCounty: level('CITY_COUNTY', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    CreditorSupplier: level('CREDITOR_SUPPLIER', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    Customer: level('CUSTOMER', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    Debtor: level('DEBTOR', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    Employee: level('EMPLOYEE', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    FinanceCompany: level('FINANCE_COMPANY', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    Other: level('OTHER', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
  })),
  Sales: level('SALES', () => ({
    NESTED: permission(NESTED_ACTION),
    Leads: level('LEADS', () => ({
      LIST: permission(PermissionActions.List),
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      APPROVE: permission(PermissionActions.Approve),
      UNAPPROVE: permission(PermissionActions.Unapprove),
      REASSIGN: permission(PermissionActions.Reassign),
      COMPLETE: permission(PermissionActions.Complete),
    })),
    Hlp: level('HLP', () => ({
      LIST: permission(PermissionActions.List),
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      APPROVE: permission(PermissionActions.Approve),
      DELETE: permission(PermissionActions.Delete),
      UNAPPROVE: permission(PermissionActions.Unapprove),
      COMPLETE: permission(PermissionActions.Complete),
    })),
    Budgets: level('BUDGETS', () => ({
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
    })),
    EmailCampaign: level('EMAIL_CAMPAIGN', () => ({
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      LIST: permission(PermissionActions.List),
    })),
  })),
  Estimating: level('ESTIMATING', () => ({
    NESTED: permission(NESTED_ACTION),
    HouseEstimate: level('HOUSE_ESTIMATE', () => ({
      Plans: level('PLANS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
      })),
      Estimate: level('ESTIMATE', () => ({
        CREATE_EDIT: permission(PermissionActions.CreateEdit),
        EDIT_QQ: permission('EDIT_QQ'),
        VIEW: permission(PermissionActions.View),
      })),
    })),
    PriceBook: level('PRICEBOOK', () => ({
      CREATE: permission(PermissionActions.Create),
      EDIT: permission(PermissionActions.Edit),
      DELETE: permission(PermissionActions.Delete),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
      REPRICE: permission('REPRICE'),
      PUBLISH: permission('PUBLISH'),
    })),
    JobEstimate: level('JOB_ESTIMATE', () => ({
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      AUTHORIZE: permission(PermissionActions.Authorize),
      REDO: permission(PermissionActions.Redo),
      LIST: permission(PermissionActions.List),
    })),
    BulkUpdate: level('BULKUPDATE', () => ({
      MANAGE: permission(PermissionActions.Manage),
    })),
    BulkDelete: level('BULKDELETE', () => ({
      HousePlans: level('HP', () => ({
        MANAGE: permission(PermissionActions.Manage),
      })),
    })),
  })),
  JobAdmin: level('JOB_ADMIN', () => ({
    NESTED: permission(NESTED_ACTION),
    Jobs: level('JOBS', () => ({
      CREATE_QUICK_JOB: permission('CREATE_QUICK_JOB'),
      CREATE_DPP: permission('CREATE_DPP'),
      LIST: permission(PermissionActions.List),
    })),
    JobDetails: level('JOB_DETAILS', () => ({
      UNCOMPLETE_COMPLETE: permission(PermissionActions.UncompleteComplete),
      UNFINALIZE: permission(PermissionActions.Unfinalize),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      REFRESH_SCHEDULE: permission('REFRESH_SCHEDULE'),
      REASSIGN: permission(PermissionActions.Reassign),
    })),
    JobSpecifications: level('JOB_SPECIFICATIONS', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
    })),
    JobColorSelection: level('JOB_COLOR_SELECTION', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
    })),
    Financials: level('FINANCIALS', () => ({
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
    })),
    JobSchedule: level('JOB_SCHEDULE', () => ({
      CREATE_EDIT: permission(PermissionActions.CreateEdit),
      VIEW: permission(PermissionActions.View),
    })),
    JobOrders: level('JOB_ORDERS', () => ({
      VIEW: permission(PermissionActions.View),
      PRODUCE: permission(PermissionActions.Produce),
      LIST: permission(PermissionActions.List),
    })),
    JobChangeOrders: level('JOB_CHANGE_ORDERS', () => ({
      CREATE: permission(PermissionActions.Create),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
    })),
  })),
  Manager: level('MANAGER', () => ({
    NESTED: permission(NESTED_ACTION),
    CashFlowForecast: level('CASH_FLOW_FORECAST', () => ({
      'MANAGE': permission(PermissionActions.Manage),
    })),
    WipProfitAdj: level('WIP_PROFIT_ADJ', () => ({
      'MANAGE': permission(PermissionActions.Manage),
    })),
    FranchiseMonthly: level('FRANCHISE_MONTHLY', () => ({
      'MANAGE': permission(PermissionActions.Manage),
    })),
    EomReports: level('EOM_REPORTS', () => ({
      'MANAGE': permission(PermissionActions.Manage),
    })),
    CareerEnquiries: level('CAREER_ENQUIRIES', () => ({
      'LIST': permission(PermissionActions.List),
    })),
  })),
  Schedules: level('SCHEDULES', () => ({
    NESTED: permission(NESTED_ACTION),
    Construction: level('CONSTRUCTION', () => ({
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      DELETE: permission(PermissionActions.Delete),
      LIST: permission(PermissionActions.List),
    })),
    PreConstruction: level('PRE_CONSTRUCTION', () => ({
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      DELETE: permission(PermissionActions.Delete),
      LIST: permission(PermissionActions.List),
    })),
    Maintenance: level('MAINTENANCE', () => ({
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      DELETE: permission(PermissionActions.Delete),
      LIST: permission(PermissionActions.List),
    })),
    Sales: level('SALES', () => ({
      CREATE: permission(PermissionActions.Create),
      VIEW: permission(PermissionActions.View),
      EDIT: permission(PermissionActions.Edit),
      DELETE: permission(PermissionActions.Delete),
      LIST: permission(PermissionActions.List),
    })),
  })),
  Accounting: level('ACCOUNTING', () => ({
    NESTED: permission(NESTED_ACTION),
    Draws: level('DRAWS', () => ({
      PRODUCE: permission(PermissionActions.Produce),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
    })),
    Sync: level('SYNC', () => ({
      INITIATE: permission(PermissionActions.Initiate),
    })),
  })),
  Setup: level('SETUP', () => ({
    NESTED: permission(NESTED_ACTION),
    ManageBilling: level('MANAGE_BILLING', () => ({
      VIEW: permission(PermissionActions.View),
      MANAGE: permission(PermissionActions.Manage),
    })),
    Manager: level('MANAGER', () => ({
      NESTED: permission(NESTED_ACTION),
      RolesAndPermissions: level('ROLES&PERMISSIONS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
      })),
      Roles: level('ROLES', () => ({
        CREATE: permission(PermissionActions.Create),
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
        LIST: permission(PermissionActions.List),
        Permissions: level('PERMISSIONS', () => ({
          VIEW: permission(PermissionActions.View),
          EDIT: permission(PermissionActions.Edit),
        })),
      })),
      UserDetails: level('USER_DETAILS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
      })),
    })),
    Sales: level('SALES', () => ({
      NESTED: permission(NESTED_ACTION),
      Communities: level('COMMUNITIES', () => ({
        LIST: permission(PermissionActions.List),
        CREATE: permission(PermissionActions.Create),
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      Checklist: level('CHECKLIST', () => ({
        LIST: permission(PermissionActions.List),
        CREATE: permission(PermissionActions.Create),
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
      })),
      ImportLeads: level('IMPORT_LEADS', () => ({
        EXECUTE: permission(PermissionActions.Execute),
      })),
    })),
    SystemVariables: level('SYSVARS', () => ({
      NESTED: permission(NESTED_ACTION),
      Franchisee: level('FRANCHISEE', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Accounting: level('ACCOUNTING', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Ledger: level('GENERAL_LEDGER', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Contacts: level('CONTACTS', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Estimating: level('ESTIMATING', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Jobs: level('JOBS', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Sales: level('SALES', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Accounts: level('ACCOUNTS', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
        INITIATE: permission(PermissionActions.Initiate),
      })),
      Payables: level('PAYABLES', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Printers: level('PRINTERS', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Receivables: level('RECEIVABLES', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Manager: level('MANAGER', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
      Supervisor: level('SUPERVISOR', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
      })),
    })),
    Contacts: level('CONTACTS', () => ({
      NESTED: permission(NESTED_ACTION),
      CityStateZipCodes: level('CITY_STATE_ZIP_CODES', () => ({
        LIST: permission(PermissionActions.List),
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
      })),
      CustomFields: level('CUSTOM_FIELDS', () => ({
        LIST: permission(PermissionActions.List),
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        DEACTIVATE: permission(PermissionActions.Deactivate),
        ACTIVATE: permission(PermissionActions.Activate),
      })),
    })),
    Estimating: level('ESTIMATING', () => ({
      NESTED: permission(NESTED_ACTION),
      UoM: level('UOM', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
      })),
      TakeOffParameters: level('TAKEOFF_PARAMETERS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      SalesTaxRates: level('SALES_TAX_RATES', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
      })),
      CostCenters: level('CC', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      Items: level('ITEMS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      Components: level('COMPONENTS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      Export: level('EXPORT', () => ({
        EXECUTE: permission(PermissionActions.Execute),
      })),
      Import: level('IMPORT', () => ({
        EXECUTE: permission(PermissionActions.Execute),
      })),
      FeeExclusions: level('FEE_EXCLUSIONS', () => ({
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
      })),
    })),
    Templates: level('TEMPLATES', () => ({
      NESTED: permission(NESTED_ACTION),
      System: level('SYSTEM', () => ({
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
      })),
      Job: level('JOB', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
      })),
      Internal: level('INTERNAL', () => ({
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
      })),
      Layout: level('LAYOUT', () => ({
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
      })),
    })),
    SmsTemplates: level('SMS_TEMPLATES', () => ({
      CREATE: permission(PermissionActions.Create),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
      DELETE: permission(PermissionActions.Delete),
    })),
    ManageTemplates: level('MANAGE_TEMPLATES', () => ({
      NESTED: permission(NESTED_ACTION),
      SystemLetters: level('SYSTEM_LETTERS', () => ({
        VIEW: permission(PermissionActions.View),
        MANAGE: permission(PermissionActions.Manage),
      })),
      JobLetters: level('JOB_LETTERS', () => ({
        VIEW: permission(PermissionActions.View),
        MANAGE: permission(PermissionActions.Manage),
      })),
    })),
    EmailRecipients: level('EMAIL_RECIPIENTS', () => ({
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
    })),
    JobAdmin: level('JOB_ADMIN', () => ({
      NESTED: permission(NESTED_ACTION),
      JobLetters: level('JOB_LETTERS', () => ({
        MANAGE: permission(PermissionActions.Manage),
      })),
      JobSpecTemplates: level('SPECIFICATION_TEMPLATES', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      ColorSelectionMasters: level('COLOR_SELECTION_MASTERS', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      JobStages: level('JOB_STAGES', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
    })),
    Accounting: level('ACCOUNTING', () => ({
      NESTED: permission(NESTED_ACTION),
      DrawRules: level('DRAW_RULES', () => ({
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        LIST: permission(PermissionActions.List),
        DELETE: permission(PermissionActions.Delete),
      })),
    })),
    MilestoneEvents: level('MILESTONE_EVENTS', () => ({
      NESTED: permission(NESTED_ACTION),
      CREATE: permission(PermissionActions.Create),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
      DELETE: permission(PermissionActions.Delete),
    })),
    Schedules: level('SCHEDULES', () => ({
      NESTED: permission(NESTED_ACTION),
      CriticalPath: level('CRITICAL_PATH', () => ({
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      ConstructionTemplates: level('CONSTRUCTION_TEMPLATES', () => ({
        LIST: permission(PermissionActions.List),
        VIEW: permission(PermissionActions.View),
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      SalesTemplates: level('SALES_TEMPLATES', () => ({
        LIST: permission(PermissionActions.List),
        VIEW: permission(PermissionActions.View),
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      PreConstructionTemplates: level('PRE_CONSTRUCTION_TEMPLATES', () => ({
        LIST: permission(PermissionActions.List),
        VIEW: permission(PermissionActions.View),
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      MaintenanceTemplates: level('MAINTENANCE_TEMPLATES', () => ({
        LIST: permission(PermissionActions.List),
        VIEW: permission(PermissionActions.View),
        CREATE: permission(PermissionActions.Create),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
        UNDELETE: permission(PermissionActions.Undelete),
      })),
      Calendar: level('CALENDAR', () => ({
        EDIT: permission(PermissionActions.Edit),
        VIEW: permission(PermissionActions.View),
        DELETE: permission(PermissionActions.Delete),
      })),
      SubContractorsSuppliers: level('SUB-CONTRACTORS_SUPPLIERS', () => ({
        VIEW: permission(PermissionActions.View),
        EDIT: permission(PermissionActions.Edit),
        DELETE: permission(PermissionActions.Delete),
      })),
    })),
    Checklists: level('CHECKLISTS', () => ({
      NESTED: permission(NESTED_ACTION),
      CREATE: permission(PermissionActions.Create),
      EDIT: permission(PermissionActions.Edit),
      VIEW: permission(PermissionActions.View),
      LIST: permission(PermissionActions.List),
      DELETE: permission(PermissionActions.Delete),
      UNDELETE: permission(PermissionActions.Undelete),
      IMPORT: permission(PermissionActions.Import),
    })),
  })),
  EventLog: level('EVENT_LOG', () => ({
    VIEW: permission(PermissionActions.View),
  })),
  BulkUpdateTaskLog: level('BULK_UPDATE_TASK_LOG', () => ({
    VIEW: permission(PermissionActions.View),
  })),
  ScheduleTaskLog: level('SCHEDULE_TASK_LOG', () => ({
    VIEW: permission(PermissionActions.View),
  })),
  XeroIssueLog: level('XERO_ISSUES_LOG', () => ({
    VIEW: permission(PermissionActions.View),
  })),
  Reports: level('REPORTS', () => ({
    JobAdmin: level('JOB_ADMIN', () => ({
      JOB_DETAILS: permission('JOB_DETAILS'),
      COLOR_SELECTION_MASTER: permission('COLOR_SELECTION_MASTER'),
      JOB_COLOR_SELECTION: permission('JOB_COLOR_SELECTION'),
      JOB_TRADING_ACCOUNT: permission('JOB_TRADING_ACCOUNT'),
      LENDERS_SCHEDULE: permission('LENDERS_SCHEDULE'),
      SALES_MANAGEMENT: permission('SALES_MANAGEMENT'),
      FINALIZED_BUDGET_ACTUAL: permission('FINALIZED_BUDGET_ACTUAL'),
      JOB_COST_SUMMARY: permission('JOB_COST_SUMMARY'),
    })),
    Estimating: level('ESTIMATING', () => ({
      GROSS_PROFIT_PRICE_BOOK: permission('GROSS_PROFIT_PRICE_BOOK'),
      HOUSE_PLAN_BILL_OF_MATERIALS: permission('HOUSE_PLAN_BILL_OF_MATERIALS'),
      JOB_BILL_OF_MATERIALS: permission('JOB_BILL_OF_MATERIALS'),
      JOB_ORDER_SUMMARY: permission('JOB_ORDER_SUMMARY'),
    })),
    Schedules: level('SCHEDULES', () => ({
      CRITICAL_PATH: permission('CRITICAL_PATH'),
      JOB_PROGRESS: permission('JOB_PROGRESS'),
      SUP_SCHEDULE: permission('SUP_SCHEDULE'),
      SCH_TEMPLATE: permission('SCH_TEMPLATE'),
      VENDOR_REPORT: permission('VENDOR_REPORT'),
      CONSTRUCTION_SCHEDULE: permission('CONSTRUCTION_SCHEDULE'),
      MOBILE_APP_CHECKLIST: permission('MOBILE_APP_CHECKLIST'),
    })),
    Manager: level('MANAGER', () => ({
      WORK_IN_PROGRESS_ADJ: permission('WORK_IN_PROGRESS_ADJ'),
      FRANCHISE_MONTHLY: permission('FRANCHISE_MONTHLY'),
      SCHEDULED_TASKS: permission('SCHEDULED TASKS'),
      YTD_AVERAGES: permission('YTD_AVERAGES'),
    })),
    Sales: level('SALES', () => ({
      SALES_MANAGEMENT: permission('SALES_MANAGEMENT'),
    })),
  })),
});

export const BuilderPermissionSet = declare({
  Estimating: level('ESTIMATING', () => ({
    HouseEstimate: level('HOUSE_ESTIMATE', () => ({
      Plans: level('PLANS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Estimating.HouseEstimate.Plans.VIEW,
          BuilderPermission.Estimating.HouseEstimate.Plans.EDIT,
        ]),
        CREATE_EDIT_VIEW: permissionSet(PermissionActions.CreateEditView, [
          BuilderPermission.Estimating.HouseEstimate.Plans.VIEW,
          BuilderPermission.Estimating.HouseEstimate.Plans.EDIT,
          BuilderPermission.Estimating.HouseEstimate.Plans.CREATE,
        ]),
      })),
    })),
    JobEstimate: level('JOB_ESTIMATE', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Estimating.JobEstimate.EDIT,
        BuilderPermission.Estimating.JobEstimate.VIEW,
      ]),
    })),
    PriceBook: level('PRICEBOOK', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Estimating.PriceBook.VIEW,
        BuilderPermission.Estimating.PriceBook.EDIT,
      ]),
      CREATE_EDIT_VIEW: permissionSet(PermissionActions.CreateEditView, [
        BuilderPermission.Estimating.PriceBook.VIEW,
        BuilderPermission.Estimating.PriceBook.EDIT,
        BuilderPermission.Estimating.PriceBook.CREATE,
      ]),
    })),
  })),
  JobAdmin: level('JOB_ADMIN', () => ({
    JobDetails: level('JOB_DETAILS', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.JobAdmin.JobDetails.EDIT,
        BuilderPermission.JobAdmin.JobDetails.VIEW,
      ]),
    })),
    JobSpecifications: level('JOB_SPECIFICATIONS', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.JobAdmin.JobSpecifications.EDIT,
        BuilderPermission.JobAdmin.JobSpecifications.VIEW,
      ]),
      LIST: permission(PermissionActions.List),
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT,
        BuilderPermission.JobAdmin.JobSpecifications.VIEW,
      ]),
    })),
    JobColorSelection: level('JOB_COLOR_SELECTION', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.JobAdmin.JobColorSelection.EDIT,
        BuilderPermission.JobAdmin.JobColorSelection.VIEW,
      ]),
      LIST: permission(PermissionActions.List),
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT,
        BuilderPermission.JobAdmin.JobColorSelection.VIEW,
      ]),
    })),
    JobSchedule: level('JOB_SCHEDULE', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.JobAdmin.JobSchedule.CREATE_EDIT,
        BuilderPermission.JobAdmin.JobSchedule.VIEW,
      ]),
    })),
    JobOrders: level('JOB_ORDERS', () => ({
      LIST: permission(PermissionActions.List),
      PRODUCE_VIEW: permissionSet(PermissionActions.ProduceView, [
        BuilderPermission.JobAdmin.JobOrders.PRODUCE,
        BuilderPermission.JobAdmin.JobOrders.VIEW,
      ]),
    })),
    JobChangeOrders: level('JOB_CHANGE_ORDERS', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.JobAdmin.JobChangeOrders.CREATE,
        BuilderPermission.JobAdmin.JobChangeOrders.EDIT,
        BuilderPermission.JobAdmin.JobChangeOrders.VIEW,
      ]),
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.JobAdmin.JobChangeOrders.VIEW,
        BuilderPermission.JobAdmin.JobChangeOrders.EDIT,
      ]),
    })),
  })),
  Schedules: level('SCHEDULES', () => ({
    Construction: level('CONSTRUCTION', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Schedules.Construction.VIEW,
        BuilderPermission.Schedules.Construction.EDIT,
      ]),
    })),
    PreConstruction: level('PRE_CONSTRUCTION', () => ({
      CREATE_EDIT_VIEW: permissionSet(PermissionActions.CreateEditView, [
        BuilderPermission.Schedules.PreConstruction.CREATE,
        BuilderPermission.Schedules.PreConstruction.VIEW,
        BuilderPermission.Schedules.PreConstruction.EDIT,
      ]),
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Schedules.PreConstruction.VIEW,
        BuilderPermission.Schedules.PreConstruction.EDIT,
      ]),
    })),
    Maintenance: level('MAINTENANCE', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Schedules.Maintenance.VIEW,
        BuilderPermission.Schedules.Maintenance.EDIT,
      ]),
    })),
    Sales: level('SALES', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Schedules.Sales.VIEW,
        BuilderPermission.Schedules.Sales.EDIT,
      ]),
    })),
  })),
  Accounting: level('ACCOUNTING', () => ({
    Draws: level('DRAWS', () => ({
      PRODUCE_VIEW: permissionSet(PermissionActions.ProduceView, [
        BuilderPermission.Accounting.Draws.PRODUCE,
        BuilderPermission.Accounting.Draws.VIEW,
      ]),
    })),
  })),
  Setup: level('SETUP', () => ({
    Estimating: level('ESTIMATING', () => ({
      CostCenters: level('CC', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.CostCenters.VIEW,
          BuilderPermission.Setup.Estimating.CostCenters.EDIT,
        ]),
      })),
      Items: level('ITEMS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.Items.VIEW,
          BuilderPermission.Setup.Estimating.Items.EDIT,
        ]),
      })),
      Components: level('COMPONENTS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.Components.VIEW,
          BuilderPermission.Setup.Estimating.Components.EDIT,
        ]),
      })),
      SalesTaxRates: level('SALES_TAX_RATES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.SalesTaxRates.VIEW,
          BuilderPermission.Setup.Estimating.SalesTaxRates.EDIT,
        ]),
      })),
      TakeOffParameters: level('TAKEOFF_PARAMETERS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.TakeOffParameters.EDIT,
          BuilderPermission.Setup.Estimating.TakeOffParameters.VIEW,
        ]),
      })),
      UoM: level('UOM', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.UoM.EDIT,
          BuilderPermission.Setup.Estimating.UoM.VIEW,
        ]),
      })),
      FeeExclusions: level('FEE_EXCLUSIONS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Estimating.FeeExclusions.EDIT,
          BuilderPermission.Setup.Estimating.FeeExclusions.VIEW,
        ]),
      })),
    })),
    JobAdmin: level('JOB_ADMIN', () => ({
      ColorSelectionMasters: level('COLOR_SELECTION_MASTER', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.JobAdmin.ColorSelectionMasters.VIEW,
          BuilderPermission.Setup.JobAdmin.ColorSelectionMasters.EDIT,
        ]),
        CREATE_EDIT: permissionSet(PermissionActions.CreateEdit, [
          BuilderPermission.Setup.JobAdmin.ColorSelectionMasters.CREATE,
          BuilderPermission.Setup.JobAdmin.ColorSelectionMasters.EDIT,
        ]),
      })),
      JobSpecTemplates: level('SPECIFICATION_TEMPLATES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.JobAdmin.JobSpecTemplates.EDIT,
          BuilderPermission.Setup.JobAdmin.JobSpecTemplates.VIEW,
        ]),
      })),
      JobStages: level('JOB_STAGES', () => ({
        ANY: permissionSet(PermissionActions.Any, [
          BuilderPermission.Setup.JobAdmin.JobStages.CREATE,
          BuilderPermission.Setup.JobAdmin.JobStages.EDIT,
          BuilderPermission.Setup.JobAdmin.JobStages.VIEW,
        ]),
      })),
    })),
    Accounting: level('ACCOUNTING', () => ({
      DrawRules: level('DRAW_RULES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Accounting.DrawRules.EDIT,
          BuilderPermission.Setup.Accounting.DrawRules.VIEW,
        ]),
      })),
    })),
    Schedules: level('SCHEDULES', () => ({
      Calendar: level('CALENDAR', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.Calendar.EDIT,
          BuilderPermission.Setup.Schedules.Calendar.VIEW,
        ]),
      })),
      CriticalPath: level('CRITICAL_PATH', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.CriticalPath.EDIT,
          BuilderPermission.Setup.Schedules.CriticalPath.VIEW,
        ]),
      })),
      ConstructionTemplates: level('CONSTRUCTION_TEMPLATES', () => ({
        ANY: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.VIEW,
        ]),
        EDIT_CREATE: permissionSet(PermissionActions.CreateEdit, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
        ]),
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.VIEW,
        ]),
      })),
      SalesTemplates: level('SALES_TEMPLATES', () => ({
        ANY: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.VIEW,
        ]),
        EDIT_CREATE: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
        ]),
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.SalesTemplates.EDIT,
          BuilderPermission.Setup.Schedules.SalesTemplates.VIEW,
        ]),
      })),
      PreConstructionTemplates: level('PRE_CONSTRUCTION_TEMPLATES', () => ({
        ANY: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.VIEW,
        ]),
        EDIT_CREATE: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
        ]),
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.PreConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.PreConstructionTemplates.VIEW,
        ]),
      })),
      MaintenanceTemplates: level('MAINTENANCE_TEMPLATES', () => ({
        ANY: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.VIEW,
        ]),
        EDIT_CREATE: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
          BuilderPermission.Setup.Schedules.ConstructionTemplates.CREATE,
        ]),
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.MaintenanceTemplates.EDIT,
          BuilderPermission.Setup.Schedules.MaintenanceTemplates.VIEW,
        ]),
      })),
      SubContractorsSuppliers: level('SUB-CONTRACTORS_SUPPLIERS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Schedules.SubContractorsSuppliers.EDIT,
          BuilderPermission.Setup.Schedules.SubContractorsSuppliers.VIEW,
        ]),
      })),
    })),
    Manager: level('MANAGER', () => ({
      UserDetails: level('USER_DETAILS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Manager.UserDetails.EDIT,
          BuilderPermission.Setup.Manager.UserDetails.VIEW,
        ]),
      })),
      Roles: level('ROLES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.Manager.Roles.EDIT,
          BuilderPermission.Setup.Manager.Roles.VIEW,
        ]),
        Permissions: level('PERMISSIONS', () => ({
          EDIT_VIEW: permissionSet(PermissionActions.EditView, [
            BuilderPermission.Setup.Manager.Roles.Permissions.EDIT,
            BuilderPermission.Setup.Manager.Roles.Permissions.VIEW,
          ]),
        })),
      })),
    })),
    SystemVariables: level('SYSVARS', () => ({
      Franchisee: level('FRANCHISEE', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Franchisee.VIEW,
          BuilderPermission.Setup.SystemVariables.Franchisee.EDIT,
        ]),
      })),
      Accounting: level('ACCOUNTING', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Accounting.VIEW,
          BuilderPermission.Setup.SystemVariables.Accounting.EDIT,
        ]),
      })),
      Sales: level('SALES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Sales.VIEW,
          BuilderPermission.Setup.SystemVariables.Sales.EDIT,
        ]),
      })),
      Jobs: level('JOBS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Jobs.VIEW,
          BuilderPermission.Setup.SystemVariables.Jobs.EDIT,
        ]),
      })),
      Estimating: level('ESTIMATING', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Estimating.VIEW,
          BuilderPermission.Setup.SystemVariables.Estimating.EDIT,
        ]),
      })),
      Accounts: level('ACCOUNTS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Accounts.VIEW,
          BuilderPermission.Setup.SystemVariables.Accounts.EDIT,
        ]),
      })),
      Printers: level('PRINTERS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Printers.VIEW,
          BuilderPermission.Setup.SystemVariables.Printers.EDIT,
        ]),
      })),
      Receivables: level('RECEIVABLES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Receivables.VIEW,
          BuilderPermission.Setup.SystemVariables.Receivables.EDIT,
        ]),
      })),
      Manager: level('MANAGER', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Manager.VIEW,
          BuilderPermission.Setup.SystemVariables.Manager.EDIT,
        ]),
      })),
      Contacts: level('CONTACTS', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Contacts.VIEW,
          BuilderPermission.Setup.SystemVariables.Contacts.EDIT,
        ]),
        CustomFields: level('CUSTOM_FIELDS', () => ({
          ANY: permissionSet(PermissionActions.Any, [
            BuilderPermission.Setup.Contacts.CustomFields.LIST,
            BuilderPermission.Setup.Contacts.CustomFields.EDIT,
            BuilderPermission.Setup.Contacts.CustomFields.CREATE,
          ]),
        })),
      })),
      Payables: level('PAYABLES', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Payables.EDIT,
          BuilderPermission.Setup.SystemVariables.Payables.VIEW,
        ]),
      })),
      Supervisor: level('SUPERVISOR', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Supervisor.VIEW,
          BuilderPermission.Setup.SystemVariables.Supervisor.EDIT,
        ]),
      })),
      Ledger: level('GENERAL_LEDGER', () => ({
        EDIT_VIEW: permissionSet(PermissionActions.EditView, [
          BuilderPermission.Setup.SystemVariables.Ledger.EDIT,
          BuilderPermission.Setup.SystemVariables.Ledger.VIEW,
        ]),
      })),
    })),
    ManageTemplates: level('MANAGE_TEMPLATES', () => ({
      SystemLetters: level('SYSTEM_LETTERS', () => ({
        ANY: permissionSet(PermissionActions.Any, [
          BuilderPermission.Setup.ManageTemplates.SystemLetters.MANAGE,
          BuilderPermission.Setup.ManageTemplates.SystemLetters.VIEW,
        ]),
      })),
      JobLetters: level('JOB_LETTERS', () => ({
        ANY: permissionSet(PermissionActions.Any, [
          BuilderPermission.Setup.ManageTemplates.JobLetters.MANAGE,
          BuilderPermission.Setup.ManageTemplates.JobLetters.VIEW,
        ]),
      })),
    })),
    MilestoneEvents: level('MILESTONE_EVENTS', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Setup.MilestoneEvents.EDIT,
        BuilderPermission.Setup.MilestoneEvents.VIEW,
      ]),
    })),
    SmsTemplates: level('SMS_TEMPLATES', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Setup.SmsTemplates.EDIT,
        BuilderPermission.Setup.SmsTemplates.VIEW,
      ]),
    })),
    EmailRecipients: level('EMAIL_RECIPIENTS', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Setup.EmailRecipients.EDIT,
        BuilderPermission.Setup.EmailRecipients.VIEW,
      ]),
    })),
    Checklists: level('CHECKLISTS', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Setup.Checklists.EDIT,
        BuilderPermission.Setup.Checklists.VIEW,
      ]),
    })),
  })),
  Contacts: level('CONTACTS', () => ({
    CityCounty: level('CITY_COUNTY', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.CityCounty.CREATE_EDIT,
        BuilderPermission.Contacts.CityCounty.VIEW,
      ]),
    })),
    CreditorSupplier: level('CREDITOR_SUPPLIER', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT,
        BuilderPermission.Contacts.CreditorSupplier.VIEW,
      ]),
    })),
    Customer: level('CUSTOMER', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.Customer.CREATE_EDIT,
        BuilderPermission.Contacts.Customer.VIEW,
      ]),
    })),
    Debtor: level('DEBTOR', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.Debtor.CREATE_EDIT,
        BuilderPermission.Contacts.Debtor.VIEW,
      ]),
    })),
    Employee: level('EMPLOYEE', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.Employee.CREATE_EDIT,
        BuilderPermission.Contacts.Employee.VIEW,
      ]),
    })),
    FinanceCompany: level('FINANCE_COMPANY', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.FinanceCompany.CREATE_EDIT,
        BuilderPermission.Contacts.FinanceCompany.VIEW,
      ]),
    })),
    Other: level('OTHER', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.Other.CREATE_EDIT,
        BuilderPermission.Contacts.Other.VIEW,
      ]),
    })),
    Details: level('DETAILS', () => ({
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Contacts.CityCounty.CREATE_EDIT,
        BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT,
        BuilderPermission.Contacts.Customer.CREATE_EDIT,
        BuilderPermission.Contacts.Debtor.CREATE_EDIT,
        BuilderPermission.Contacts.Employee.CREATE_EDIT,
        BuilderPermission.Contacts.FinanceCompany.CREATE_EDIT,
        BuilderPermission.Contacts.Other.CREATE_EDIT,
        BuilderPermission.Contacts.CityCounty.VIEW,
        BuilderPermission.Contacts.CreditorSupplier.VIEW,
        BuilderPermission.Contacts.Customer.VIEW,
        BuilderPermission.Contacts.Debtor.VIEW,
        BuilderPermission.Contacts.Employee.VIEW,
        BuilderPermission.Contacts.FinanceCompany.VIEW,
        BuilderPermission.Contacts.Other.VIEW,
      ]),
      CREATE_EDIT: permissionSet(PermissionActions.CreateEdit, [
        BuilderPermission.Contacts.CityCounty.CREATE_EDIT,
        BuilderPermission.Contacts.CreditorSupplier.CREATE_EDIT,
        BuilderPermission.Contacts.Customer.CREATE_EDIT,
        BuilderPermission.Contacts.Debtor.CREATE_EDIT,
        BuilderPermission.Contacts.Employee.CREATE_EDIT,
        BuilderPermission.Contacts.FinanceCompany.CREATE_EDIT,
        BuilderPermission.Contacts.Other.CREATE_EDIT,
      ]),
      VIEW: permissionSet(PermissionActions.View, [
        BuilderPermission.Contacts.CityCounty.VIEW,
        BuilderPermission.Contacts.CreditorSupplier.VIEW,
        BuilderPermission.Contacts.Customer.VIEW,
        BuilderPermission.Contacts.Debtor.VIEW,
        BuilderPermission.Contacts.Employee.VIEW,
        BuilderPermission.Contacts.FinanceCompany.VIEW,
        BuilderPermission.Contacts.Other.VIEW,
      ]),
    })),
  })),
  Sales: level('SALES', () => ({
    Leads: level('LEADS', () => ({
      CREATE_EDIT: permissionSet(PermissionActions.CreateEdit, [
        BuilderPermission.Sales.Leads.CREATE,
        BuilderPermission.Sales.Leads.EDIT,
      ]),
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Sales.Leads.EDIT,
        BuilderPermission.Sales.Leads.VIEW,
      ]),
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Sales.Leads.EDIT,
        BuilderPermission.Sales.Leads.VIEW,
        BuilderPermission.Sales.Leads.LIST,
        BuilderPermission.Sales.Leads.CREATE,
      ]),
    })),
    Hlp: level('HLP', () => ({
      CREATE_EDIT: permissionSet(PermissionActions.CreateEdit, [
        BuilderPermission.Sales.Hlp.CREATE,
        BuilderPermission.Sales.Hlp.EDIT,
      ]),
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Sales.Hlp.EDIT,
        BuilderPermission.Sales.Hlp.VIEW,
      ]),
      ANY: permissionSet(PermissionActions.Any, [
        BuilderPermission.Sales.Hlp.EDIT,
        BuilderPermission.Sales.Hlp.VIEW,
        BuilderPermission.Sales.Hlp.LIST,
        BuilderPermission.Sales.Hlp.CREATE,
      ]),
    })),
    EmailCampaign: level('EMAIL_CAMPAIGN', () => ({
      EDIT_VIEW: permissionSet(PermissionActions.EditView, [
        BuilderPermission.Sales.EmailCampaign.EDIT,
        BuilderPermission.Sales.EmailCampaign.VIEW,
      ]),
    })),
  })),
  XeroIssueLog: level('XERO_ISSUES_LOG', () => ({
    VIEW: permissionSet(PermissionActions.View, [
      BuilderPermission.XeroIssueLog.VIEW,
    ]),
  })),
});
